import { Group, Paper, Text, UnstyledButton } from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from '@tabler/icons-react';
import { useId, useState } from 'react';
import { formatClock } from 'src/common/utils/formatClock';
import { formatDate } from 'src/meetings/utils/date-utils';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Waveform } from './Waveform';
import { waveformBars } from './waveformBars';
import classes from './AudioTranscriptCard.module.css';

type AudioTranscriptSegment = {
  id: number;
  speaker: string;
  text: string;
  startSeconds: number;
  endSeconds: number;
};

/** Which meeting this moment came from — a report can be written from several, and from
 *  different years, so the label carries the year rather than just the day and month. */
function recordingLabel(name: string, date: string | null): string {
  const formatted = formatDate(date);
  return formatted ? `Meeting – ${formatted}` : name;
}

type AudioTranscriptCardProps = {
  segment: AudioTranscriptSegment;
  recordingName?: string;
  recordingDate?: string | null;
  isPlaying: boolean;
  /** Playback position within this segment, 0..1. */
  progress: number;
  onTogglePlay: () => void;
};

// Tuned for the narrow Siesta chat panel; bars flex to fill whatever width the card gets.
const BAR_COUNT = 56;

export function AudioTranscriptCard({
  segment,
  recordingName,
  recordingDate,
  isPlaying,
  progress,
  onTogglePlay,
}: AudioTranscriptCardProps) {
  const [expanded, setExpanded] = useState(false);
  const transcriptId = useId();

  const clampedProgress = Math.min(1, Math.max(0, progress));
  const bars = waveformBars(segment.id, BAR_COUNT);
  const duration = Math.max(0, segment.endSeconds - segment.startSeconds);
  const clock = formatClock(isPlaying ? clampedProgress * duration : duration);

  const transcript = segment.text.trim();

  return (
    <Paper bg="contentBackground.0" p="sm" withBorder style={{ borderRadius: 16 }}>
      <Group justify="space-between" align="center" wrap="nowrap" mb="xs">
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.7">
          {segment.speaker}
        </Text>
        {recordingName && (
          <Text size="textXs" c="gray.6" ta="right">
            {recordingLabel(recordingName, recordingDate ?? null)}
          </Text>
        )}
      </Group>

      <Group gap="sm" align="center" wrap="nowrap">
        <UnstyledButton
          type="button"
          className={classes.play}
          onClick={onTogglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <IconPlayerPauseFilled size={16} aria-hidden />
          ) : (
            <IconPlayerPlayFilled size={18} aria-hidden />
          )}
        </UnstyledButton>
        <Waveform bars={bars} progress={clampedProgress} />
      </Group>

      <Group justify="space-between" align="center" mt="xs">
        <Text
          size="textXs"
          fw={FONT_WEIGHTS.semibold}
          c="gray.6"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {clock}
        </Text>
        <UnstyledButton
          type="button"
          className={classes.toggle}
          onClick={() => setExpanded(value => !value)}
          aria-expanded={expanded}
          aria-controls={transcriptId}
        >
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="brand.7">
            {expanded ? 'Hide transcription' : 'Show transcription'}
          </Text>
          {expanded ? (
            <IconChevronUp size={14} aria-hidden />
          ) : (
            <IconChevronDown size={14} aria-hidden />
          )}
        </UnstyledButton>
      </Group>

      {expanded && transcript && (
        <Text id={transcriptId} size="textSm" c="gray.8" mt="sm" className={classes.quote}>
          &ldquo;{transcript}&rdquo;
        </Text>
      )}
    </Paper>
  );
}
